"use client";

import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, Suspense, useContext, useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter, useSearchParams } from "next/navigation";
import { DEMO_ACCOUNT_ID, DEMO_USER_EMAIL } from "@/constants";
import { toast } from "sonner";
import { cn, presetColorThemes, setPlatformForAccount } from "@/lib/utils";
import { Button } from "@/components/presentational/tailwind/Button";
import { useQueryClient } from "@tanstack/react-query";
import { AccountByIdData, useGetAccountById, useUpdateAccountMutation } from "@/utils/react-query-hooks";
import { useConfettiContext } from "@/contexts/ConfettiContextProvider";
import { requestStartGrowth, requestPauseGrowth } from "@/utils/start-growth-client";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { AccountContext } from "../AccountIdLayoutContext";
import { match } from "path-to-regexp";
import { usePathname } from "next/navigation";
import { UserContext } from "@/app/AppProviders";
import { UserProfileContext } from "@/app/app/ClientLayout";
import AccountDropdown from "@/components/presentational/tailwind/AccountDropdown";
import TimeFrameLineTabs from "@/components/presentational/tailwind/TimeFrameLineTabs";
// Tooltip import removed - using MobileTooltip for touch-safe tooltips
import { AreaChart as RechartsAreaChart, Area, ResponsiveContainer } from "recharts";
import { ArrowSmallUpIcon } from "@heroicons/react/20/solid";
import { PlayCircleIcon } from "@heroicons/react/24/solid";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import PageTitle from "@/components/presentational/tailwind/PageTitle";
import { AI_TRAINING_STATES, FRAME_DURATION } from "@/components/ui/ai-training-frames";
import { MotionGrid, MotionGridCells } from "@/components/ui/motion-grid";
import { ShimmeringText } from "@/components/ui/shimmering-text";
import { useDemoContext } from "@/contexts/DemoContext";
import { isTrialExpired } from "@/utils/account-type";
import { MobileTooltip, MobileTooltipContent, MobileTooltipTrigger } from "@/components/ui/mobile-tooltip";
import { AIActivityCard } from "@/components/outfame/AIActivityCard";
// import QuestionMarkIcon from '@public/img/icons/question-mark.svg';

// Lazy load DemoActivitySimulator for demo subdomain only
const DemoActivitySimulator = dynamic(() => import("@/app/app/components/DemoActivitySimulator"), {
  ssr: false
});
export function AccountClientLayout(t0) {
  const $ = _c(151);
  const {
    children,
    initialAccountByIdData,
    accountId
  } = t0;
  const router = useRouter();
  useContext(UserProfileContext);
  useContext(UserContext);
  const {
    isDemoMode
  } = useDemoContext();
  const {
    data: account,
    isLoading,
    error
  } = useGetAccountById(accountId, initialAccountByIdData);
  let t1;
  let t2;
  if ($[0] !== account || $[1] !== isLoading || $[2] !== router) {
    t1 = () => {
      if (!isLoading && !account) {
        router.push("/app");
      }
    };
    t2 = [account, isLoading, router];
    $[0] = account;
    $[1] = isLoading;
    $[2] = router;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== account || $[6] !== accountId) {
    t3 = () => {
      if (account?.platform && accountId) {
        setPlatformForAccount(accountId, account.platform);
      }
    };
    t4 = [account, accountId];
    $[5] = account;
    $[6] = accountId;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  const searchParams = useSearchParams();
  let t5;
  if ($[9] !== searchParams) {
    t5 = searchParams.get("upgraded");
    $[9] = searchParams;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const justUpgraded = t5 === "true";
  const queryClient = useQueryClient();
  let t6;
  if ($[11] !== account) {
    t6 = account?.analytics && typeof account.analytics === "object" && Object.keys(account.analytics).length > 0;
    $[11] = account;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const hasAnalytics = !!t6;
  let t7;
  let t8;
  if ($[13] !== accountId || $[14] !== hasAnalytics || $[15] !== justUpgraded || $[16] !== queryClient) {
    t7 = () => {
      if (!justUpgraded || hasAnalytics) {
        return;
      }
      const interval = setInterval(() => {
        queryClient.invalidateQueries(["account", accountId]);
      }, 2000);
      const timeout = setTimeout(() => clearInterval(interval), 30000);
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    };
    t8 = [justUpgraded, hasAnalytics, accountId, queryClient];
    $[13] = accountId;
    $[14] = hasAnalytics;
    $[15] = justUpgraded;
    $[16] = queryClient;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  const [demoActiveOverride, setDemoActiveOverride] = useState(null);
  const [warmupScore, setWarmupScore] = useState(0);
  let t10;
  let t9;
  if ($[19] !== account || $[20] !== demoActiveOverride || $[21] !== isDemoMode) {
    t9 = () => {
      if (!account?.created_at) {
        return;
      }
      const currentActiveState = isDemoMode && demoActiveOverride !== null ? demoActiveOverride : account?.active;
      if (!currentActiveState) {
        const isNeverStarted = account.account_type === "trial" && !isDemoMode;
        return setWarmupScore(isNeverStarted ? -2 : -1);
      }
      const minutesSinceSignUp = (Date.now() - new Date(account.created_at).getTime()) / 60000;
      const warmupScores = [{
        limit: 10,
        score: 10
      }, {
        limit: 30,
        score: 15
      }, {
        limit: 60,
        score: 20
      }, {
        limit: 180,
        score: 30
      }, {
        limit: 480,
        score: 35
      }, {
        limit: 720,
        score: 40
      }, {
        limit: 1440,
        score: 50
      }, {
        limit: 2160,
        score: 55
      }, {
        limit: 2880,
        score: 60
      }, {
        limit: 4320,
        score: 70
      }, {
        limit: 5760,
        score: 75
      }, {
        limit: 7200,
        score: 77
      }, {
        limit: 8640,
        score: 79
      }, {
        limit: 10080,
        score: 80
      }, {
        limit: 13440,
        score: 82
      }, {
        limit: 20160,
        score: 85
      }, {
        limit: 26880,
        score: 87
      }, {
        limit: 30240,
        score: 90
      }, {
        limit: 33600,
        score: 92
      }, {
        limit: 36960,
        score: 95
      }, {
        limit: 40320,
        score: 97
      }, {
        limit: 43680,
        score: 98
      }];
      setWarmupScore(warmupScores.find(score => minutesSinceSignUp <= score.limit)?.score || 99);
    };
    t10 = [account, isDemoMode, demoActiveOverride];
    $[19] = account;
    $[20] = demoActiveOverride;
    $[21] = isDemoMode;
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  useEffect(t9, t10);
  const t11 = account?.id;
  let t12;
  if ($[24] !== account?.id) {
    t12 = () => requestPauseGrowth(account?.id);
    $[24] = account?.id;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = {
      accountId: t11,
      hideToast: true,
      persist: t12
    };
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const {
    mutate: mutateAccount,
    isLoading: isLoadingAccountUpdate
  } = useUpdateAccountMutation(t13);
  const {
    openUpgradeModal,
    setTrialExpired
  } = useModalContext();
  let t14;
  if ($[29] !== account) {
    t14 = account ? isTrialExpired(account) : false;
    $[29] = account;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const trialExpired = t14;
  let t15;
  let t16;
  if ($[31] !== setTrialExpired || $[32] !== trialExpired) {
    t15 = () => {
      setTrialExpired(trialExpired);
      return () => setTrialExpired(false);
    };
    t16 = [trialExpired, setTrialExpired];
    $[31] = setTrialExpired;
    $[32] = trialExpired;
    $[33] = t15;
    $[34] = t16;
  } else {
    t15 = $[33];
    t16 = $[34];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[35] !== account || $[36] !== isDemoMode || $[37] !== openUpgradeModal || $[38] !== trialExpired) {
    t17 = () => {
      if (!account || account.account_type !== "trial" || isDemoMode) {
        return;
      }
      if (trialExpired) {
        const timer = setTimeout(() => openUpgradeModal(account.id), 300);
        return () => clearTimeout(timer);
      }
      const lastDismissed = localStorage.getItem("outfame_upgrade_modal_dismissed_at");
      const cooldownExpired = !lastDismissed || parseInt(lastDismissed) < Date.now() - 300000;
      if (cooldownExpired) {
        const isNewSignup = account.created_at && Date.now() - new Date(account.created_at).getTime() < 120000;
        const delay = isNewSignup ? 6000 : 500;
        const timer_0 = setTimeout(() => openUpgradeModal(account.id), delay);
        return () => clearTimeout(timer_0);
      }
    };
    t18 = [account, isDemoMode, openUpgradeModal, trialExpired];
    $[35] = account;
    $[36] = isDemoMode;
    $[37] = openUpgradeModal;
    $[38] = trialExpired;
    $[39] = t17;
    $[40] = t18;
  } else {
    t17 = $[39];
    t18 = $[40];
  }
  useEffect(t17, t18);
  const {
    setShowConfetti
  } = useConfettiContext();
  const isActive = isDemoMode && demoActiveOverride !== null ? demoActiveOverride : account?.active ?? true;
  const [isButtonReady, setIsButtonReady] = useState(false);
  const hasInitialData = useRef(!!initialAccountByIdData);
  let t19;
  let t20;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      const delay_0 = hasInitialData.current ? 50 : 0;
      const timer_1 = setTimeout(() => setIsButtonReady(true), delay_0);
      return () => clearTimeout(timer_1);
    };
    t20 = [];
    $[41] = t19;
    $[42] = t20;
  } else {
    t19 = $[41];
    t20 = $[42];
  }
  useEffect(t19, t20);
  const pathname = usePathname() || "";
  let t21;
  if ($[43] !== pathname) {
    const matchSettingsPath = match("/app/account/:accountId/settings/(.*)?");
    t21 = matchSettingsPath(pathname);
    $[43] = pathname;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const isSettingsPath = t21;
  let t22;
  if ($[45] !== pathname) {
    const matchAnalyticsPath = match("/app/account/:accountId/analytics/(.*)?");
    t22 = matchAnalyticsPath(pathname);
    $[45] = pathname;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  const isAnalyticsPath = t22;
  let t23;
  if ($[47] !== pathname) {
    const matchSchedulePath = match("/app/account/:accountId/schedule/(.*)?");
    t23 = matchSchedulePath(pathname);
    $[47] = pathname;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  const isSchedulePath = t23;
  let t24;
  if ($[49] !== pathname) {
    const matchAiTrainingPath = match("/app/account/:accountId/ai-training/(.*)?");
    t24 = matchAiTrainingPath(pathname);
    $[49] = pathname;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  const isAiTrainingPath = t24;
  let t25;
  if ($[51] !== pathname) {
    const matchMaxPath = match("/app/account/:accountId/max/(.*)?");
    t25 = matchMaxPath(pathname);
    $[51] = pathname;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  const isMaxPath = t25;
  let t26;
  if ($[53] !== pathname) {
    const matchAiContentPath = match("/app/account/:accountId/ai-content/(.*)?");
    t26 = matchAiContentPath(pathname);
    $[53] = pathname;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  const isAiContentPath = t26;
  let t27;
  if ($[55] !== pathname) {
    t27 = pathname.includes("/ai-content/slideshow");
    $[55] = pathname;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  const isAiContentSlideshowPath = t27;
  let t28;
  if ($[57] !== pathname) {
    t28 = pathname.includes("/ai-content/reaction-video");
    $[57] = pathname;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  const isAiContentReactionPath = t28;
  let t29;
  if ($[59] !== pathname) {
    const matchSlideshowPath = match("/app/account/:accountId/slideshow/(.*)?");
    t29 = matchSlideshowPath(pathname);
    $[59] = pathname;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  const isSlideshowPath = t29;
  let t30;
  if ($[61] !== pathname) {
    const matchViralPath = match("/app/account/:accountId/viral/(.*)?");
    t30 = matchViralPath(pathname);
    $[61] = pathname;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  const isViralPath = t30;
  let t31;
  if ($[63] !== pathname) {
    const matchTiktokAccountsPath = match("/app/account/:accountId/tiktok-accounts/(.*)?");
    t31 = matchTiktokAccountsPath(pathname);
    $[63] = pathname;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  const isTiktokAccountsPath = t31;
  const isDemoAccount = account && account.id === DEMO_ACCOUNT_ID;
  const isOnContentPage = isAiContentPath || isSchedulePath || isViralPath || isSlideshowPath || isTiktokAccountsPath;
  const isOnGrowthPage = !isOnContentPage;
  let t32;
  if ($[65] !== isAiContentPath || $[66] !== isAiContentReactionPath || $[67] !== isAiContentSlideshowPath || $[68] !== isAiTrainingPath || $[69] !== isAnalyticsPath || $[70] !== isMaxPath || $[71] !== isSchedulePath || $[72] !== isSettingsPath || $[73] !== isSlideshowPath || $[74] !== isViralPath) {
    t32 = () => {
      if (isAnalyticsPath) {
        return "Analytics";
      }
      if (isSettingsPath) {
        return "Settings";
      }
      if (isSchedulePath) {
        return "Schedule";
      }
      if (isAiTrainingPath) {
        return "AI Training";
      }
      if (isMaxPath) {
        return "Max";
      }
      if (isAiContentSlideshowPath) {
        return "TikTok Slideshow";
      }
      if (isAiContentReactionPath) {
        return "Reaction Video Editor";
      }
      if (isAiContentPath) {
        return "AI Content";
      }
      if (isSlideshowPath) {
        return "Slideshow";
      }
      if (isViralPath) {
        return "Viral Content Library";
      }
      return "Growth";
    };
    $[65] = isAiContentPath;
    $[66] = isAiContentReactionPath;
    $[67] = isAiContentSlideshowPath;
    $[68] = isAiTrainingPath;
    $[69] = isAnalyticsPath;
    $[70] = isMaxPath;
    $[71] = isSchedulePath;
    $[72] = isSettingsPath;
    $[73] = isSlideshowPath;
    $[74] = isViralPath;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  const getPageTitle = t32;
  let t33;
  if ($[76] !== isAiContentReactionPath || $[77] !== isAiContentSlideshowPath || $[78] !== isViralPath) {
    t33 = () => {
      if (isViralPath) {
        return "Browse trending content for inspiration";
      }
      if (isAiContentSlideshowPath) {
        return "Create viral slideshows with AI captions";
      }
      if (isAiContentReactionPath) {
        return "Create UGC-style reaction videos with AI actors";
      }
    };
    $[76] = isAiContentReactionPath;
    $[77] = isAiContentSlideshowPath;
    $[78] = isViralPath;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  const getPageSubtitle = t33;
  let t34;
  if ($[80] !== account || $[81] !== isActive || $[82] !== isDemoMode || $[83] !== mutateAccount || $[84] !== openUpgradeModal || $[85] !== queryClient || $[86] !== setShowConfetti) {
    t34 = function toggleActiveAccount() {
      if (account?.account_type === "trial" && !isDemoMode) {
        console.log("Upgrade account modal...................");
        openUpgradeModal(account?.id);
      } else {
        if (isDemoMode) {
          const newActiveState = !isActive;
          setDemoActiveOverride(newActiveState);
          toast.success(isActive ? "Growth paused successfully!" : "Growth started successfully!", {
            id: "toggleActiveAccount"
          });
          if (newActiveState) {
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 3000);
          }
        } else {
          const wasActive = account?.active;
          toast.loading(wasActive ? "Pausing growth..." : "Starting growth...", {
            id: "toggleActiveAccount"
          });
          if (!wasActive && account?.id) {
            const accountIdToStart = account.id;
            requestStartGrowth(accountIdToStart).then(result => {
              if (result.ok) {
                toast.success("Growth started successfully!", {
                  id: "toggleActiveAccount"
                });
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 3000);
              } else {
                toast.error(result.message, {
                  id: "toggleActiveAccount"
                });
              }
              queryClient.invalidateQueries(["account", accountIdToStart]);
              queryClient.invalidateQueries(["all-accounts"]);
            });
            return;
          }
          mutateAccount({
            active: !wasActive
          }, {
            onSuccess: () => {
              toast.success(wasActive ? "Growth paused successfully!" : "Growth started successfully!", {
                id: "toggleActiveAccount"
              });
              if (!wasActive) {
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 3000);
              }
            },
            onError: _temp
          });
        }
      }
    };
    $[80] = account;
    $[81] = isActive;
    $[82] = isDemoMode;
    $[83] = mutateAccount;
    $[84] = openUpgradeModal;
    $[85] = queryClient;
    $[86] = setShowConfetti;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  const toggleActiveAccount = t34;
  let t35;
  if ($[88] !== account?.platform) {
    t35 = themeName_0 => {
      if (account?.platform === "tiktok") {
        return "from-[#F5A3B2]/50";
      }
      return (presetColorThemes.find(theme_0 => theme_0.name === themeName_0) || presetColorThemes[0]).pageShadowGradient;
    };
    $[88] = account?.platform;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  const getShadowGradientThemeClass = t35;
  const HtmlContent = _temp2;
  const getWarmupConfig = _temp3;
  const warmupConfig = getWarmupConfig(warmupScore);
  const [aiStateIndex, setAiStateIndex] = useState(0);
  let t36;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = Object.keys(AI_TRAINING_STATES);
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  const aiStateKeys = t36 as (keyof typeof AI_TRAINING_STATES)[];
  let t37;
  let t38;
  if ($[91] !== warmupScore) {
    t37 = () => {
      if (warmupScore === -1 || warmupScore === -2) {
        return;
      }
      const interval_0 = setInterval(() => setAiStateIndex(i => (i + 1) % aiStateKeys.length), 3000);
      return () => clearInterval(interval_0);
    };
    t38 = [warmupScore];
    $[91] = warmupScore;
    $[92] = t37;
    $[93] = t38;
  } else {
    t37 = $[92];
    t38 = $[93];
  }
  useEffect(t37, t38);
  const currentAiState = AI_TRAINING_STATES[aiStateKeys[aiStateIndex]];
  let t39;
  if ($[94] !== warmupScore) {
    t39 = () => {
      if (warmupScore === -2) {
        return "bg-amber-400";
      }
      if (warmupScore === -1) {
        return "bg-gray-300";
      }
      if (warmupScore > 85) {
        return "bg-emerald-500";
      }
      if (warmupScore > 50) {
        return "bg-gray-900";
      }
      return "bg-gray-400";
    };
    $[94] = warmupScore;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  const getCellColor = t39;
  let t40;
  if ($[96] !== isAnalyticsPath) {
    t40 = () => {
      if (isAnalyticsPath) {
        return [{
          label: "Overview",
          active: true
        }, {
          label: "Reports",
          active: false
        }];
      }
    };
    $[96] = isAnalyticsPath;
    $[97] = t40;
  } else {
    t40 = $[97];
  }
  const getPageTabs = t40;
  let t41;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="relative h-10 w-[140px] overflow-hidden rounded-xl bg-gray-100"><div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" /></div>;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  const SkeletonButtonDesktop = t41;
  const showButtonSkeleton = !isButtonReady || isLoading;
  let t42;
  if ($[99] !== isActive || $[100] !== isLoadingAccountUpdate || $[101] !== showButtonSkeleton || $[102] !== toggleActiveAccount) {
    t42 = <AnimatePresence mode="wait">{showButtonSkeleton ? <motion.div key="skeleton" initial={{
        opacity: 1
      }} exit={{
        opacity: 0,
        filter: "blur(4px)"
      }} transition={{
        duration: 0.2,
        ease: "easeOut"
      }}>{SkeletonButtonDesktop}</motion.div> : <motion.button data-confetti-origin="true" key="button" initial={{
        opacity: 0,
        filter: "blur(4px)"
      }} animate={{
        opacity: 1,
        filter: "blur(0px)"
      }} transition={{
        duration: 0.35,
        ease: [0.25, 0.1, 0.25, 1]
      }} onClick={() => toggleActiveAccount()} disabled={isLoadingAccountUpdate} className={cn("group relative inline-flex h-10 items-center justify-center gap-2 overflow-hidden rounded-xl px-5", "text-[15px] font-bold", "transition-all duration-200 ease-out", "focus:outline-none focus:ring-2", "active:scale-[0.97]", isActive ? "bg-gray-900 text-white hover:bg-gray-800 focus:ring-gray-200 px-4 btn-inset-dark" : "bg-[rgb(255,163,209)] text-gray-900 hover:bg-[#fc83bf] hover:shadow-[0_0_24px_rgba(255,163,209,0.4)] focus:ring-pink-200", isLoadingAccountUpdate && "pointer-events-none opacity-70")}>{isActive ? <><span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" /></span><ShimmeringText text="Growing" color="#ffffff" shimmeringColor="rgba(255,255,255,0.4)" duration={1.5} interval={6} className="text-[15px] font-bold" /><span className="mx-0.5 h-3.5 w-px bg-white/20" /><span className="text-white/70 transition-colors group-hover:text-white">Pause</span></> : <><span className="absolute inset-0 -z-5 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[100%]" /><PlayCircleIcon className="h-4 w-4" /><span>Start Growth</span></>}</motion.button>}</AnimatePresence>;
    $[99] = isActive;
    $[100] = isLoadingAccountUpdate;
    $[101] = showButtonSkeleton;
    $[102] = toggleActiveAccount;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  const StartPauseButtonDesktop = t42;
  let t43;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="relative h-14 w-full overflow-hidden rounded-xl bg-gray-100"><div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" /></div>;
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  const SkeletonButtonMobile = t43;
  let t44;
  if ($[105] !== isActive || $[106] !== isLoadingAccountUpdate || $[107] !== showButtonSkeleton || $[108] !== toggleActiveAccount) {
    t44 = <AnimatePresence mode="wait">{showButtonSkeleton ? <motion.div key="skeleton-mobile" initial={{
        opacity: 1
      }} exit={{
        opacity: 0,
        filter: "blur(4px)"
      }} transition={{
        duration: 0.2,
        ease: "easeOut"
      }} className="w-full">{SkeletonButtonMobile}</motion.div> : <motion.button data-confetti-origin="true" key="button-mobile" initial={{
        opacity: 0,
        filter: "blur(4px)"
      }} animate={{
        opacity: 1,
        filter: "blur(0px)"
      }} transition={{
        duration: 0.35,
        ease: [0.25, 0.1, 0.25, 1]
      }} onClick={() => toggleActiveAccount()} disabled={isLoadingAccountUpdate} className={cn("group relative inline-flex h-14 w-full items-center justify-center gap-2.5 overflow-hidden rounded-xl px-6", "text-base font-bold", "transition-all duration-200 ease-out", "focus:outline-none focus:ring-2", "active:scale-[0.97]", isActive ? "bg-gray-900 text-white btn-inset-dark" : "bg-[rgb(255,163,209)] text-gray-900", isLoadingAccountUpdate && "pointer-events-none opacity-70")}>{isActive ? <><span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" /></span><ShimmeringText text="Growing" color="#ffffff" shimmeringColor="rgba(255,255,255,0.4)" duration={1.5} interval={6} className="text-base font-bold" /><span className="mx-1 h-4 w-px bg-white/20" /><span className="text-white/70">Pause</span></> : <><span className="absolute inset-0 -z-5 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[100%]" /><span className="relative flex items-center justify-center"><span className="absolute h-5 w-5 animate-ping rounded-full bg-gray-900/20" /><PlayCircleIcon className="relative h-5 w-5" /></span><span>Start Growth</span></>}</motion.button>}</AnimatePresence>;
    $[105] = isActive;
    $[106] = isLoadingAccountUpdate;
    $[107] = showButtonSkeleton;
    $[108] = toggleActiveAccount;
    $[109] = t44;
  } else {
    t44 = $[109];
  }
  const StartPauseButtonMobile = t44;
  let t45;
  if ($[110] !== StartPauseButtonDesktop || $[111] !== StartPauseButtonMobile) {
    t45 = () => [{
      label: "Start Growth",
      customElement: StartPauseButtonDesktop,
      customMobileElement: StartPauseButtonMobile
    }];
    $[110] = StartPauseButtonDesktop;
    $[111] = StartPauseButtonMobile;
    $[112] = t45;
  } else {
    t45 = $[112];
  }
  const getPageActions = t45;
  const t46 = getPageTitle();
  let t47;
  if ($[113] !== getPageSubtitle) {
    t47 = getPageSubtitle();
    $[113] = getPageSubtitle;
    $[114] = t47;
  } else {
    t47 = $[114];
  }
  let t48;
  if ($[115] !== getPageTabs) {
    t48 = getPageTabs();
    $[115] = getPageTabs;
    $[116] = t48;
  } else {
    t48 = $[116];
  }
  let t49;
  if ($[117] !== getPageActions) {
    t49 = getPageActions();
    $[117] = getPageActions;
    $[118] = t49;
  } else {
    t49 = $[118];
  }
  let t50;
  if ($[119] !== t46 || $[120] !== t47 || $[121] !== t48 || $[122] !== t49) {
    t50 = <div className="sticky top-0 z-40 border-b border-gray-200/60 bg-white"><div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"><PageTitle title={t46} subtitle={t47} tabs={t48} actions={t49} className="border-b-0" /></div></div>;
    $[119] = t46;
    $[120] = t47;
    $[121] = t48;
    $[122] = t49;
    $[123] = t50;
  } else {
    t50 = $[123];
  }
  const t51 = "relative pb-0 pt-4";
  let t52;
  if ($[124] !== account?.theme_color || $[125] !== getShadowGradientThemeClass || $[126] !== isActive) {
    t52 = !isActive && <div className={cn("pointer-events-none absolute -top-0.5 left-auto right-0 z-0 hidden h-[84%] w-[35%] rounded-[21px] via-white to-transparent blur-[5px] md:block", getShadowGradientThemeClass(account?.theme_color))} style={{
      backgroundImage: "linear-gradient(to bottom left, var(--tw-gradient-stops))"
    }} />;
    $[124] = account?.theme_color;
    $[125] = getShadowGradientThemeClass;
    $[126] = isActive;
    $[127] = t52;
  } else {
    t52 = $[127];
  }
  const t53 = "mx-auto w-full max-w-6xl";
  const t54 = isOnGrowthPage && <div className="z-1 relative flex flex-col gap-4 px-2 sm:px-6 lg:px-8">{isLoading ? <div className="flex w-full min-w-0 flex-wrap gap-3 lg:flex-nowrap"><div className="card h-[72px] flex-1 animate-pulse !p-0 lg:w-44 lg:flex-none" /><div className="card h-[72px] flex-1 animate-pulse !p-0 lg:w-44 lg:flex-none" /><div className="card h-[56px] basis-full animate-pulse !p-0 lg:basis-auto lg:h-[72px] lg:flex-1" /></div> : <div className="flex w-full min-w-0 animate-fade-in flex-wrap gap-3 lg:flex-nowrap">{(() => {
        const metric = account?.followers || 0;
        const previousMetric = isDemoAccount ? account?.followers * 0.8 : account?.starting_followers || 0;
        let percentageDifference = previousMetric > 0 ? (metric - previousMetric) / previousMetric * 100 : 0;
        if (!isFinite(percentageDifference)) {
          percentageDifference = 0;
        }
        const isPositive = percentageDifference > 0;
        const isNegative = percentageDifference < 0;
        return <div className="card !mb-0 flex flex-1 items-center gap-3 !p-3 sm:justify-start sm:!px-4 sm:!py-3 lg:flex-none"><div className="flex min-w-0 flex-col"><span className="text-xs font-medium uppercase tracking-wide text-gray-400">Followers</span><div className="flex items-baseline gap-1.5"><span className="text-[22px] font-bold tabular-nums text-gray-900">{metric.toLocaleString()}</span><span className={cn("text-xs font-semibold", isPositive ? "text-green-600" : isNegative ? "text-red-500" : "text-gray-400")}>{percentageDifference >= 0 ? "+" : ""}{percentageDifference.toFixed(0)}%</span></div></div><div className="h-6 flex-1 sm:h-8 lg:w-16 lg:flex-none"><ResponsiveContainer width="100%" height="100%"><RechartsAreaChart data={percentageDifference == 0 ? [{
                value: 1
              }, {
                value: 1
              }] : isPositive ? [{
                value: 2
              }, {
                value: 3
              }] : [{
                value: 3
              }, {
                value: 2
              }]} margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 0
              }}><defs><linearGradient id="sparkline-gradient" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={isPositive ? "#22c55e" : isNegative ? "#ef4444" : "#9ca3af"} stopOpacity={0.2} /><stop offset="100%" stopColor={isPositive ? "#22c55e" : isNegative ? "#ef4444" : "#9ca3af"} stopOpacity={0} /></linearGradient></defs><Area type="monotone" dataKey="value" stroke={isPositive ? "#22c55e" : isNegative ? "#ef4444" : "#9ca3af"} strokeWidth={1.5} fill="url(#sparkline-gradient)" /></RechartsAreaChart></ResponsiveContainer></div></div>;
      })()}<MobileTooltip delayDuration={0} disableHoverableContent={true}><MobileTooltipTrigger asChild={true}><div className={cn("card !mb-0 flex flex-1 cursor-help items-center justify-between gap-3 !p-3 sm:justify-start sm:!px-4 sm:!py-3 lg:flex-none", "transition-colors duration-200")}><div className="flex min-w-0 flex-col"><span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-gray-400">AI Training</span><div className="flex items-end gap-2"><span className={cn("text-[22px] font-bold tabular-nums", warmupConfig.color)}>{warmupScore < 0 ? "\u2014" : `${warmupScore}%`}</span><div className="relative mb-[6px] h-[14px] w-[62px] shrink-0 overflow-hidden sm:hidden"><AnimatePresence mode="popLayout"><motion.span key={warmupScore < 0 ? warmupConfig.label : currentAiState.label} initial={{
                      opacity: 0,
                      y: "100%"
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: "-100%"
                    }} transition={{
                      duration: 0.25,
                      ease: [0.4, 0, 0.2, 1]
                    }} className="absolute inset-0 flex items-end whitespace-nowrap text-[12px] font-medium leading-none"><ShimmeringText text={warmupScore < 0 ? warmupConfig.label : `${currentAiState.label}...`} duration={0.8} wave={false} color="#374151" shimmeringColor="#9ca3af" /></motion.span></AnimatePresence></div></div></div><div className="shrink-0"><MotionGrid gridSize={[5, 5]} frames={AI_TRAINING_STATES[aiStateKeys[aiStateIndex]].frames} duration={FRAME_DURATION} animate={warmupScore >= 0} className="gap-[3px]"><MotionGridCells className={cn("h-[5px] w-[5px] rounded-full", getCellColor())} /></MotionGrid></div><div className="relative hidden h-[20px] w-[90px] shrink-0 overflow-hidden sm:block"><AnimatePresence mode="popLayout"><motion.span key={warmupScore < 0 ? warmupConfig.label : currentAiState.label} initial={{
                  opacity: 0,
                  y: "100%"
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: "-100%"
                }} transition={{
                  duration: 0.25,
                  ease: [0.4, 0, 0.2, 1]
                }} className="absolute inset-0 flex items-center whitespace-nowrap text-[14px] font-semibold"><ShimmeringText text={warmupScore < 0 ? warmupConfig.label : `${currentAiState.label}...`} duration={0.8} wave={true} color="#374151" shimmeringColor="#9ca3af" /></motion.span></AnimatePresence></div></div></MobileTooltipTrigger><MobileTooltipContent side="bottom" className="max-w-[300px]"><HtmlContent html={warmupScore === -2 ? "Your AI is ready to start learning your niche, audience, and content style. Start growth to begin training." : warmupScore === -1 ? "Your AI is paused. Resume growth to continue optimizing your targeting and engagement strategy." : warmupScore > 85 ? "Your AI has mastered your account \u2014 analyzing competitors, optimizing engagement timing, and targeting followers most likely to engage with your content. Operating at peak performance." : warmupScore > 50 ? `Your AI is actively studying your niche right now — analyzing engagement patterns, mapping your ideal audience, and refining its targeting with every interaction. At ${warmupScore}%, it's already delivering results and getting smarter every hour.` : "Your AI is learning your account \u2014 scanning your content, identifying your niche, and building an audience profile. Growth improves rapidly as training progresses."} /></MobileTooltipContent></MobileTooltip><AIActivityCard accountType={account?.account_type || "trial"} active={account?.active ?? false} /></div>}</div>;
  let t55;
  if ($[128] !== account?.id || $[129] !== isAnalyticsPath) {
    t55 = isAnalyticsPath && <div className="px-4 sm:px-6 lg:px-8"><TimeFrameLineTabs accountId={account?.id} onTabChange={_temp4} /></div>;
    $[128] = account?.id;
    $[129] = isAnalyticsPath;
    $[130] = t55;
  } else {
    t55 = $[130];
  }
  let t56;
  if ($[131] !== t54 || $[132] !== t55) {
    t56 = <div className={t53}>{t54}{t55}</div>;
    $[131] = t54;
    $[132] = t55;
    $[133] = t56;
  } else {
    t56 = $[133];
  }
  let t57;
  if ($[134] !== t52 || $[135] !== t56) {
    t57 = <section className={t51}>{t52}{t56}</section>;
    $[134] = t52;
    $[135] = t56;
    $[136] = t57;
  } else {
    t57 = $[136];
  }
  let t58;
  if ($[137] !== account || $[138] !== error || $[139] !== isLoading) {
    t58 = {
      account,
      isLoading,
      error
    };
    $[137] = account;
    $[138] = error;
    $[139] = isLoading;
    $[140] = t58;
  } else {
    t58 = $[140];
  }
  let t59;
  if ($[141] !== children || $[142] !== t58) {
    t59 = <section className="bg-dashbg py-6"><div className="mx-auto max-w-6xl px-2 sm:px-6 lg:px-8"><AccountContext.Provider value={t58}>{children}</AccountContext.Provider></div></section>;
    $[141] = children;
    $[142] = t58;
    $[143] = t59;
  } else {
    t59 = $[143];
  }
  let t60;
  if ($[144] !== accountId) {
    t60 = <DemoActivitySimulator accountId={accountId} />;
    $[144] = accountId;
    $[145] = t60;
  } else {
    t60 = $[145];
  }
  let t61;
  if ($[146] !== t50 || $[147] !== t57 || $[148] !== t59 || $[149] !== t60) {
    t61 = <>{t50}{t57}{t59}{t60}</>;
    $[146] = t50;
    $[147] = t57;
    $[148] = t59;
    $[149] = t60;
    $[150] = t61;
  } else {
    t61 = $[150];
  }
  return t61;
}
function _temp4(index) {
  console.log(`Changed tab to ${index}`);
}
function _temp3(score_0) {
  if (score_0 === -2) {
    return {
      label: "Waiting...",
      color: "text-amber-500",
      barColor: "bg-amber-300"
    };
  }
  if (score_0 === -1) {
    return {
      label: "Paused",
      color: "text-gray-500",
      barColor: "bg-gray-300"
    };
  }
  if (score_0 > 85) {
    return {
      label: "Ready",
      color: "text-green-600",
      barColor: "bg-green-500"
    };
  }
  if (score_0 > 50) {
    return {
      label: "Training",
      color: "text-gray-900",
      barColor: "bg-gray-900"
    };
  }
  return {
    label: "Starting",
    color: "text-gray-500",
    barColor: "bg-gray-400"
  };
}
function _temp2(t0) {
  const {
    html
  } = t0;
  return <div dangerouslySetInnerHTML={{
    __html: html
  }} />;
}
function _temp() {
  toast.error("Failed to update growth status", {
    id: "toggleActiveAccount"
  });
}